// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import ConfirmDialog from '../../components/ui/ConfirmDialog'

/** Who may cancel a visit — the server's gate (CareRxPotCancel), mirrored so the button only shows to them. */
export function canCancelVisits(role: string | null | undefined): boolean {
  return role === 'pharmacy_staff' || role === 'admin'
}

/**
 * Go-live feedback #5 — cancel an open POT visit (scheduled / claimed) that will not happen, with a
 * required reason. Before this the only way out of My work was "Complete", which records a visit that
 * never took place and feeds the agency invoice. Used by the POT queue and by My work; renders nothing
 * for a role that cannot cancel.
 */
export default function CancelVisitButton({ visitId, label, onCancelled, compact = false }: {
  visitId: string
  /** Names the visit in the dialog (patient · visit type) — the list row already shows it. */
  label?: string
  onCancelled: () => void
  compact?: boolean
}) {
  const { role } = useAuth()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (!canCancelVisits(role)) return null

  const confirm = async ({ reason }: { reason: string }) => {
    setBusy(true); setError(null)
    try {
      await api.cancelCareRxVisit(visitId, reason)
      setOpen(false)
      onCancelled()
    } catch (e) {
      const b = (e as { body?: { message?: string } }).body
      setError(b?.message ?? friendlyError(e))
    } finally { setBusy(false) }
  }

  return (
    <>
      <button type="button" disabled={busy} onClick={e => { e.stopPropagation(); setError(null); setOpen(true) }}
        title="Cancel this visit — it will not take place (reason required)"
        style={{ fontSize: compact ? 11 : 12, cursor: 'pointer', border: '1px solid #fecaca', color: '#b91c1c',
                 background: 'transparent', borderRadius: 6, padding: compact ? '2px 8px' : '3px 10px', flexShrink: 0 }}>
        Cancel visit
      </button>
      <ConfirmDialog open={open} tone="danger" title="Cancel this visit?"
        body={<>{label && <b>{label}</b>}{label && <br />}
          The visit leaves the POT queue and My work and is not billed. The record and its history are kept.</>}
        requireReason={{ label: 'Reason (required)', placeholder: 'e.g. enrollment withdrawn, patient declined the visit' }}
        confirmLabel="Cancel visit" cancelLabel="Keep visit" busy={busy} busyLabel="Cancelling…" error={error}
        onConfirm={confirm} onCancel={() => setOpen(false)} />
    </>
  )
}
